import React, { useCallback, useContext, useEffect, useState } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { OkButton, Dialog as DialogComponent, Form, CancelLink, PickerInput, DataColumnDef, InlineOverlay, ValidationMessage } from "@plex/react-components";

import { commonMessages, ILocale } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { ISettingsState, settingsActions, Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation/dialog";
import { useLocalizedValues } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { appLanguageSwitchActions } from "./AppLanguageSwitch.actions";
import styles from "./AppLanguageSwitch.module.scss";
import { IAppLanguageSwitchState } from "./AppLanguageSwitch.state";

export interface IAppLanguageSwitchProps {
  currentLocaleIdentifier: string;
  loadSupportedLocales: () => void;
  loadSearchedLocales: (criterion: IPickerSearchCriterion<ILocale>) => void;
  supportedLocales?: ILocale[];
  searchedLocales: ILocale[];
  changeLocale: (locale: string) => void;
  resetState: () => void;
  isLoading?: boolean;
  notifications: INotification[];
}

export const AppLanguageSwitch: React.FC<IAppLanguageSwitchProps> = ({
  currentLocaleIdentifier,
  supportedLocales,
  searchedLocales,
  changeLocale,
  loadSupportedLocales,
  loadSearchedLocales,
  resetState,
  notifications
}) => {
  const dialogNavigationContext = useContext(DialogNavigationContext);

  const [selectedLocale, setSelectedLocale] = useState<ILocale[]>([]);
  const [localePickerInitialized, setLocalePickerInitialized] = useState<boolean>();
  const [isValid, setIsValid] = useState<boolean>(true);

  const localizedValues = useLocalizedValues(() => ({
    chooseLocalePickerDialogTitle: commonMessages.title("chooseLocalePickerDialog"),
    chooseLocaleTitle: commonMessages.title("chooseLocale"),
    chooseLocaleFieldText: commonMessages.heading("chooseLocaleField"),
    localeIdentifier: commonMessages.property("localeIdentifier"),
    localeName: commonMessages.property("localeName"),
    localeRequired: glossaryMessages.error("localeRequired")
  }));

  useEffect(() => {
    loadSupportedLocales();
  }, [loadSupportedLocales]);

  useEffect(() => {
    if (supportedLocales && !localePickerInitialized && currentLocaleIdentifier) {
      setSelectedLocale(supportedLocales.filter(e => e.localeIdentifier === currentLocaleIdentifier));
      setLocalePickerInitialized(true);
    }
  }, [supportedLocales, currentLocaleIdentifier, localePickerInitialized]);

  const close = useCallback(() => {
    dialogNavigationContext.hideDialog();

    resetState();
  }, [resetState, dialogNavigationContext]);

  const applyLanguageChange = useCallback(() => {
    if (isValid) {
      changeLocale(selectedLocale[0].localeIdentifier);

      close();
    }
  }, [changeLocale, selectedLocale, close, isValid]);

  const onLocaleSelectionChanged = useCallback((selection: ILocale[]) => {
    if (selection && selection.length) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }

    setSelectedLocale(selection);
  }, []);

  return (
    <DialogContainer
      onHidden={resetState}
      render={dialogProps => {
        return (
          <DialogComponent {...dialogProps} minHeight={250}>
            <DialogComponent.Body>
              <Form
                onSubmit={e => {
                  e.preventDefault();
                }}
              >
                <Form.Header>
                  <Banner messages={notifications} />
                </Form.Header>
                <Form.Body>
                  <Form.Section title={localizedValues.chooseLocaleTitle} isCollapsed={false}>
                    <Form.Row>
                      <Form.FieldPair className={styles.languageField} labelText={localizedValues.chooseLocaleFieldText} required id="app-lanquage-switch-locale-picker">
                        <InlineOverlay className={styles.languageFieldLoadingOverlay} spinnerPosition={"left"} show={!localePickerInitialized} showSpinner>
                          <PickerInput
                            autoPick
                            dialogTitle={localizedValues.chooseLocalePickerDialogTitle}
                            name="languagePicker"
                            data={searchedLocales}
                            keySelector={(row: ILocale) => row.localeIdentifier}
                            displaySelector={(row: ILocale) => row.name}
                            selected={selectedLocale}
                            onSelectionChanged={rows => onLocaleSelectionChanged(rows as ILocale[])}
                            onSearch={(q, cols) => loadSearchedLocales({ query: q, searchedColumns: cols })}
                          >
                            <DataColumnDef<ILocale>
                              id="localeIdentifier"
                              title={localizedValues.localeIdentifier}
                              valueSelector={(row: ILocale) => row.localeIdentifier}
                              searchable
                              sortable
                            >
                              {(row: ILocale) => row.localeIdentifier}
                            </DataColumnDef>
                            <DataColumnDef<ILocale> id="name" title={localizedValues.localeName} valueSelector={row => row.name} searchable sortable>
                              {(row: ILocale) => row.name}
                            </DataColumnDef>
                          </PickerInput>
                        </InlineOverlay>
                        {!isValid && <ValidationMessage message={localizedValues.localeRequired} />}
                      </Form.FieldPair>
                    </Form.Row>
                  </Form.Section>
                </Form.Body>
              </Form>
            </DialogComponent.Body>
            <DialogComponent.Footer>
              <CancelLink onClick={close} />
              <OkButton onClick={applyLanguageChange} />
            </DialogComponent.Footer>
          </DialogComponent>
        );
      }}
    />
  );
};

export default connect(
  (state: { settings: ISettingsState; appLanguageSwitch: IAppLanguageSwitchState }) => {
    return {
      currentLocaleIdentifier: state.settings.localSettings.locale,
      supportedLocales: state.appLanguageSwitch.supportedLocales,
      searchedLocales: state.appLanguageSwitch.searchedLocales,
      notifications: state.appLanguageSwitch.notifications
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        changeLocale: settingsActions.changeLocale,
        loadSupportedLocales: appLanguageSwitchActions.loadSupportedLocales,
        loadSearchedLocales: appLanguageSwitchActions.loadSearchedLocales,
        resetState: appLanguageSwitchActions.resetState
      },
      dispatch
    );
  }
)(AppLanguageSwitch);
